import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError, mapFieldErrors } from 'core/errors/mapFieldErrors';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import { TagChip } from 'features/result-content/TagChip';
import { SingleTagSelect } from 'features/result-content/TagPicker';
import {
  RESULT_ITEM_TYPES,
  buildResultItemPayload,
  isSelectableTag,
  localizedCopy,
  requiresSkillAdvice,
  typeLabelKey,
  validateResultItemForm,
} from 'features/result-content/resultContent';
import { Button } from 'shared/components/Button';
import { Badge, EmptyState, ErrorState, Skeleton } from 'shared/components/Feedback';
import { Pagination } from 'shared/components/Pagination';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { useToast } from 'shared/components/Toast';
import { BilingualField, SelectField, SwitchField, TextField } from 'shared/forms/Fields';
import { useUnsavedChanges } from 'shared/utilities/useUnsavedChanges';

const emptyForm = {
  type: 'key_strength',
  title_ar: '',
  title_en: '',
  description_ar: '',
  description_en: '',
  advice_ar: '',
  advice_en: '',
  tag_id: '',
  is_active: true,
};

export function ResultItemsPanel({ t, locale, dir, canManage, type }) {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ page: 1, per_page: 20, search: '', tag_id: '', is_active: '', type });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm, type });
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);
  useUnsavedChanges(Boolean(showForm && dirty));

  useEffect(() => {
    setFilters((current) => ({ ...current, type, page: 1 }));
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyForm, type });
    setDirty(false);
  }, [type]);

  const tagsQuery = useQuery({
    queryKey: queryKeys.tags,
    queryFn: ({ signal }) => assessmentApi.listTags({ signal }),
  });
  const query = useQuery({
    queryKey: queryKeys.resultItems(filters),
    queryFn: ({ signal }) => assessmentApi.listResultItems(filters, { signal }),
  });

  const tags = tagsQuery.data?.items || [];
  const items = useMemo(() => query.data?.items || [], [query.data]);
  const meta = query.data?.meta || {};

  useEffect(() => {
    if (!editingId) {
      return;
    }
    const item = items.find((row) => String(row.id) === String(editingId));
    if (!item) {
      return;
    }
    setForm({
      type: item.type || type,
      title_ar: item.title_ar || '',
      title_en: item.title_en || '',
      description_ar: item.description_ar || '',
      description_en: item.description_en || '',
      advice_ar: item.advice_ar || '',
      advice_en: item.advice_en || '',
      tag_id: item.tag?.id ? String(item.tag.id) : '',
      is_active: Boolean(item.is_active),
    });
    setDirty(false);
    setErrors({});
  }, [editingId, items, type]);

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['assessment', 'result-items'] }),
      queryClient.invalidateQueries({ queryKey: ['assessment', 'top-three-profiles'] }),
      queryClient.invalidateQueries({ queryKey: queryKeys.overview }),
    ]);
  };

  const save = useMutation({
    mutationFn: (body) => (editingId ? assessmentApi.updateResultItem(editingId, body) : assessmentApi.createResultItem(body)),
    onSuccess: async () => {
      setDirty(false);
      setShowForm(false);
      setEditingId(null);
      setForm({ ...emptyForm, type });
      await invalidate();
      toast.success(editingId ? t('status.updated') : t('status.created'));
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isValidation()) setErrors(mapFieldErrors(error.errors));
      toast.error(humanizeApiError(error, t));
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }) => assessmentApi.setResultItemActivation(id, isActive),
    onSuccess: invalidate,
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });

  const removal = useMutation({
    mutationFn: (id) => assessmentApi.deleteResultItem(id),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('status.deleted'));
    },
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });

  const updateField = (key, value) => {
    setDirty(true);
    setForm((current) => {
      if (key === 'type' && !requiresSkillAdvice(value)) {
        return { ...current, type: value, advice_ar: '', advice_en: '' };
      }
      return { ...current, [key]: value };
    });
  };

  const onSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateResultItemForm(form, t);
    const selectedTag = tags.find((tag) => String(tag.id) === String(form.tag_id));
    if (selectedTag && !isSelectableTag(selectedTag)) {
      nextErrors.tag_id = [t('resultContent.inactiveOrArchivedTag')];
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    save.mutate(buildResultItemPayload(form));
  };

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;

  return (
    <div>
      <div className="toolbar wrap">
        <TextField id={`${type}-search`} label={t('actions.search')} value={filters.search} onChange={(value) => setFilters((current) => ({ ...current, search: value, page: 1 }))} />
        <SelectField
          id={`${type}-tag`}
          label={t('resultContent.filterByTag')}
          value={filters.tag_id}
          onChange={(value) => setFilters((current) => ({ ...current, tag_id: value, page: 1 }))}
          options={[{ value: '', label: t('filters.all') }, ...tags.map((tag) => ({ value: String(tag.id), label: localizedCopy(tag, locale, 'name') }))]}
        />
        <SelectField
          id={`${type}-status`}
          label={t('actions.filter')}
          value={filters.is_active}
          onChange={(value) => setFilters((current) => ({ ...current, is_active: value, page: 1 }))}
          options={[
            { value: '', label: t('filters.all') },
            { value: 'true', label: t('filters.active') },
            { value: 'false', label: t('filters.inactive') },
          ]}
        />
        {canManage ? (
          <Button
            onClick={() => {
              setEditingId(null);
              setForm({ ...emptyForm, type });
              setErrors({});
              setShowForm(true);
              setDirty(false);
            }}
          >
            {t('resultItems.create')}
          </Button>
        ) : null}
      </div>
      {showForm && canManage ? (
        <form className="card stack-form" onSubmit={onSubmit}>
          <h2>{editingId ? t('actions.edit') : t('resultItems.create')}</h2>
          <SelectField
            id={`${type}-form-type`}
            label={t('resultItems.type')}
            value={form.type}
            onChange={(value) => updateField('type', value)}
            options={RESULT_ITEM_TYPES.map((itemType) => ({ value: itemType, label: t(typeLabelKey(itemType)) }))}
            error={errors.type?.[0]}
            required
          />
          <BilingualField
            id={`${type}-title`}
            labelAr={t('resultItems.titleAr')}
            labelEn={t('resultItems.titleEn')}
            valueAr={form.title_ar}
            valueEn={form.title_en}
            onChangeAr={(value) => updateField('title_ar', value)}
            onChangeEn={(value) => updateField('title_en', value)}
            errorAr={errors.title_ar?.[0]}
            errorEn={errors.title_en?.[0]}
          />
          <BilingualField
            id={`${type}-description`}
            labelAr={t('resultItems.descriptionAr')}
            labelEn={t('resultItems.descriptionEn')}
            valueAr={form.description_ar}
            valueEn={form.description_en}
            onChangeAr={(value) => updateField('description_ar', value)}
            onChangeEn={(value) => updateField('description_en', value)}
            errorAr={errors.description_ar?.[0]}
            errorEn={errors.description_en?.[0]}
            multiline
          />
          {requiresSkillAdvice(form.type) ? (
            <BilingualField
              id={`${type}-advice`}
              labelAr={t('resultItems.adviceAr')}
              labelEn={t('resultItems.adviceEn')}
              valueAr={form.advice_ar}
              valueEn={form.advice_en}
              onChangeAr={(value) => updateField('advice_ar', value)}
              onChangeEn={(value) => updateField('advice_en', value)}
              errorAr={errors.advice_ar?.[0]}
              errorEn={errors.advice_en?.[0]}
              multiline
            />
          ) : null}
          <SingleTagSelect
            id={`${type}-tag-select`}
            label={t('summaries.tags')}
            tags={tags}
            value={form.tag_id}
            onChange={(value) => updateField('tag_id', value)}
            locale={locale}
            t={t}
            error={errors.tag_id}
            required
            currentTag={items.find((item) => String(item.id) === String(editingId))?.tag}
          />
          <SwitchField id={`${type}-active`} label={t('patterns.active')} checked={form.is_active} onChange={(value) => updateField('is_active', value)} />
          <div className="toolbar">
            <Button type="submit" loading={save.isPending} disabled={save.isPending}>{t('actions.save')}</Button>
            <Button tone="ghost" onClick={() => { setShowForm(false); setDirty(false); }}>{t('actions.cancel')}</Button>
          </div>
        </form>
      ) : null}
      {items.length === 0 ? <EmptyState title={t('empty.resultItems')} /> : (
        <div className="card-list">
          {items.map((item) => (
            <article key={item.id} className="card">
              <div className="toolbar wrap">
                <Badge>{t(typeLabelKey(item.type))}</Badge>
                <Badge tone={item.is_active ? 'success' : 'neutral'}>{item.is_active ? t('filters.active') : t('filters.inactive')}</Badge>
              </div>
              <h2>{localizedCopy(item, locale)}</h2>
              <p>{localizedCopy(item, locale, 'description')}</p>
              {requiresSkillAdvice(item.type) && (item.advice_ar || item.advice_en) ? (
                <p><strong>{t('resultItems.skillAdvice')}:</strong> {localizedCopy(item, locale, 'advice')}</p>
              ) : null}
              <TagChip tag={item.tag} locale={locale} t={t} />
              {item.usage_count != null ? <p className="muted">{t('resultContent.usageCount', { count: item.usage_count })}</p> : null}
              {canManage ? (
                <div className="toolbar">
                  <Button tone="ghost" onClick={() => { setEditingId(item.id); setShowForm(true); }}>{t('actions.edit')}</Button>
                  <Button tone="ghost" disabled={toggle.isPending} onClick={() => toggle.mutate({ id: item.id, isActive: !item.is_active })}>
                    {item.is_active ? t('actions.deactivate') : t('actions.activate')}
                  </Button>
                  <Button
                    tone="danger"
                    disabled={removal.isPending}
                    onClick={async () => {
                      if (await confirm({ body: t('confirm.delete') })) removal.mutate(item.id);
                    }}
                  >
                    {t('actions.archive')}
                  </Button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}
      <Pagination
        page={meta.current_page || filters.page}
        lastPage={meta.last_page}
        total={meta.total}
        onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
        label={t('pagination.page')}
        ofLabel={t('pagination.of')}
        previousLabel={t('pagination.previous')}
        nextLabel={t('pagination.next')}
        dir={dir}
      />
    </div>
  );
}
